/**
 * Compact typing indicator for Word Add-in task pane.
 * Shows pulsating dots and who is currently typing.
 */

import { useTranslation } from 'react-i18next'
import type { TypingUser } from '../../hooks/useNegotiationSSE'

interface TypingIndicatorProps {
  typingUsers: TypingUser[]
  /** Current actor's ID to filter out self */
  currentActorId?: string
}

export function TypingIndicator({ typingUsers, currentActorId }: TypingIndicatorProps) {
  const { t } = useTranslation()

  // Filter out current user from typing list
  const others = currentActorId
    ? typingUsers.filter((u) => u.actor_id !== currentActorId)
    : typingUsers

  if (others.length === 0) return null

  // Build display text
  let text: string
  if (others.length === 1) {
    text = t('comments.typingOne', '{{name}} is typing...', { name: others[0].display_name })
  } else if (others.length === 2) {
    text = t('comments.typingTwo', '{{name1}} and {{name2}} are typing...', {
      name1: others[0].display_name,
      name2: others[1].display_name,
    })
  } else {
    text = t('comments.typingMany', '{{count}} people are typing...', { count: others.length })
  }

  return (
    <div role="status" aria-live="polite" className="shrink-0 px-2 py-1 text-[9px] text-neutral-500 dark:text-neutral-400 flex items-center gap-1">
      {/* Pulsating dots */}
      <div className="flex gap-0.5">
        <span
          className="w-1 h-1 bg-primary-500 rounded-full animate-bounce"
          style={{ animationDelay: '0ms', animationDuration: '0.6s' }}
        />
        <span
          className="w-1 h-1 bg-primary-500 rounded-full animate-bounce"
          style={{ animationDelay: '150ms', animationDuration: '0.6s' }}
        />
        <span
          className="w-1 h-1 bg-primary-500 rounded-full animate-bounce"
          style={{ animationDelay: '300ms', animationDuration: '0.6s' }}
        />
      </div>
      <span className="italic truncate">{text}</span>
    </div>
  )
}
